import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { IAuthStore } from '../../../../__legacy__/modules/auth/auth.store';
import { IOverlayLoaderStore } from '../../../common/OverlayLoader.store';
import { Settings } from '../../../settings/SettingsContext';
import { PredictionImages } from '../../components/images/PredictionImages';
import { IJobDetailsService } from '../../jobDetails.service';
import { INFERENCE_TYPE, JOB_STATUS, JOB_TYPE } from '../../jobs.model';
import { IJobThresholdStore } from '../../jobThreshold.store';
import { getJobRoutePrefix } from '../../utils';
import { withValidType } from './withValidType';

export const JobImagesContainer = withValidType(
  observer((): ReactElement => {
    const jobDetailsService = useInjection(IJobDetailsService.$);
    const authStore = useInjection(IAuthStore.$);
    const overlayLoader = useInjection(IOverlayLoaderStore.$);
    const thresholdStore = useInjection(IJobThresholdStore.$);
    const settingsContext = useContext(Settings);
    const location = useLocation();

    const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
      await jobDetailsService.changePaginationAsync(pageNumber, pageSize);
      settingsContext.setModelsImagesPageSize(pageSize);
    };

    const handleSwitchChange = async (): Promise<void> => {
      await jobDetailsService.togglePredictionsAsync();
    };

    const {
      updateThresholdAsync,
      store: {
        images: { paging, images },
        job: { status, variant, type },
        jobType,
      },
    } = jobDetailsService;
    const { threshold, setThresholdRange, setOptimalThreshold } = thresholdStore;

    const isLoading =
      status === JOB_STATUS.UNKNOWN || overlayLoader.isSpinnerVisible('job-images-loader') || overlayLoader.isSpinnerVisible('job-images-predictions-loader');
    const showSwitchGroup = !matchPath(location.pathname, getJobRoutePrefix(jobType).Create.Images.Path)?.isExact;
    const disableSwitch = ![JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED].includes(status);
    const allowImageDetails = [JOB_STATUS.FINISHED, JOB_STATUS.IMPORTED].includes(status);

    return (
      <PredictionImages
        jobType={jobType}
        allowImageDetails={allowImageDetails}
        isLoading={isLoading}
        pagination={{
          pageNumber: paging.pageNumber,
          pageSize: paging.pageSize,
          totalCount: paging.totalCount,
          onChange: handlePaginationChange,
        }}
        showPredictions={paging.fetchPredictions}
        onSwitchChange={handleSwitchChange}
        detailsImages={images}
        authToken={authStore.token}
        showSwitchGroup={showSwitchGroup}
        disableSwitch={disableSwitch}
        modelVariant={variant}
        thresholdConfig={threshold}
        setThresholdRange={setThresholdRange}
        setOptimalThreshold={setOptimalThreshold}
        updateThresholdAsync={jobType === JOB_TYPE.TRAINING ? updateThresholdAsync : undefined}
        status={status}
        isInferenceOnDataset={type === INFERENCE_TYPE.DATASET}
      />
    );
  })
);
